import { Button, Text } from "@telegram-apps/telegram-ui";
import React from "react";
import { useParams } from "react-router";

import {
  addExpense,
  getExpense,
  removeExpense,
  updateExpense,
} from "../../api/expense";
import { Expense, InputExpense } from "../../types";
import BottomButtons from "../BottomButtons";
import Popup from "../Popup";
import useBackButton from "../useBackButton";
import useExpenseView from "./useExpenseView";

interface DetailProps {
  // supplied as props when opened from a startapp deep link, and by the router otherwise
  id?: string | number;
  // the notification message this was opened from, removed together with the expense
  messageId?: number;
}

export default function Detail({ id, messageId }: DetailProps) {
  // `id` only arrives as a prop from a view-expense deep link; reached through the router instead,
  // there is a screen behind this one to go back to
  useBackButton({ enabled: id === undefined });

  const { id: idFromParams } = useParams();
  const expenseId = React.useMemo(() => {
    const raw = id ?? idFromParams;
    if (raw === undefined) {
      return undefined;
    }
    const parsed = typeof raw === "number" ? raw : parseInt(raw);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [id, idFromParams]);

  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

  const [popup, setPopup] = React.useState<React.ReactNode>(null);

  const { component, expense: editedExpense, callbacks } = useExpenseView();

  const showRevertPopup = React.useCallback(() => {
    setPopup(<Popup children={<Text>支出を復元しました</Text>} />);
  }, []);

  const showDeletePopup = React.useCallback(
    (deletedExpense: InputExpense) => {
      setPopup(
        <Popup
          children={<Text>支出を削除しました</Text>}
          revert={async () => {
            const expense = {
              ...deletedExpense,
              amount: deletedExpense.amount!,
              id: undefined,
            } as Expense;

            await addExpense(expense);
            showRevertPopup();
          }}
        />
      );
    },
    [showRevertPopup]
  );

  React.useEffect(() => {
    if (!expenseId) {
      return;
    }
    getExpense(expenseId).then(callbacks.setExpense);
  }, [expenseId, callbacks]);

  return (
    <>
      {component}
      {popup ||
        (expenseId && (
          <>
            <BottomButtons>
              {/* margin rather than `gap`: flexbox gap is unsupported on older iOS WebViews */}
              <span style={{ flex: "0 0 20%", marginRight: 10 }}>
                <Button
                  size="l"
                  loading={isDeleting}
                  disabled={isDeleting}
                  onClick={async () => {
                    setIsDeleting(true);
                    await removeExpense(expenseId, messageId);
                    setIsDeleting(false);
                    showDeletePopup(editedExpense);
                  }}
                  style={{ width: "100%", backgroundColor: "#fa5f5f" }}
                >
                  🗑️
                </Button>
              </span>
              <span style={{ flex: "1 1 auto" }}>
                <Button
                  size="l"
                  loading={isUpdating}
                  disabled={isUpdating || editedExpense.amount === undefined}
                  onClick={async () => {
                    setIsUpdating(true);
                    if (editedExpense.amount) {
                      await updateExpense(expenseId, {
                        ...editedExpense,
                        amount: editedExpense.amount,
                      });
                      setIsUpdating(false);
                    }
                  }}
                  style={{ width: "100%" }}
                >
                  Save
                </Button>
              </span>
            </BottomButtons>
          </>
        ))}
    </>
  );
}
